import { Link } from 'react-router-dom'
import { useDailySnapshots, usePhaseStatus, type Phase } from '../lib/api'
import { useAuth } from '../lib/auth'
import { AppShell, type PageKey } from '../components/AppShell'
import { EquityChart } from '../components/EquityChart'

interface ViewProps { phase: Phase }

const PHASE_COPY: Record<Phase, { eyebrow: string; subtitle: (d: number | null) => string }> = {
  p1: { eyebrow: 'Phase 1 · Challenge',     subtitle: (d) => d != null ? `${d} days remaining to hit the profit target.` : 'Build profit, avoid the drawdown floor.' },
  p2: { eyebrow: 'Phase 2 · Verification',  subtitle: (d) => d != null ? `${d} days remaining · diversify your markets.` : 'Smaller target, prove consistency.' },
  p3: { eyebrow: 'Phase 3 · Review',        subtitle: () => 'Manual review in progress — we are inspecting your bets.' },
  p4: { eyebrow: 'Phase 4 · Funded',        subtitle: () => 'Funded account · profit split active.' },
  closed: { eyebrow: 'Account closed',      subtitle: () => 'This challenge has ended.' },
}

// PhaseView is the Dashboard route (/), so the active nav must always be
// "dashboard". The /challenge route uses its own ChallengePage component.
const PHASE_TO_PAGE: Record<Phase, PageKey> = {
  p1: 'dashboard', p2: 'dashboard', p3: 'dashboard', p4: 'dashboard', closed: 'dashboard',
}

function fmtEur(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

const clampPct = (n: number) => Math.max(0, Math.min(100, n))

const InfoIcon = () => (<svg className="kpi-card__info" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="9"/><path d="M12 8 v.01 M12 11 v6"/></svg>)
const TrendUp   = () => (<svg className="kpi-card__delta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="6 14 12 8 18 14"/></svg>)
const TrendDown = () => (<svg className="kpi-card__delta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="6 10 12 16 18 10"/></svg>)

interface KpiProps {
  label: string; tooltip?: string
  variant?: 'primary' | 'profit' | 'risk' | 'time' | 'count'
  riskLevel?: 'low' | 'medium' | 'high'
  value: string; valueSecondary?: string
  delta?: { dir: 'up' | 'down'; label: string }
  progressPct: number; progressLeftMeta?: string; progressRightMeta?: string
}

function KpiCard({ label, tooltip, variant, riskLevel, value, valueSecondary, delta, progressPct, progressLeftMeta, progressRightMeta }: KpiProps) {
  const cls = ['kpi-card']
  if (variant) cls.push(`kpi-card--${variant}`)
  if (variant === 'profit') cls.push('kpi-card--primary')
  return (
    <article className={cls.join(' ')} data-risk={riskLevel}>
      <header className="kpi-card__header">
        <h2 className="kpi-card__label">{label}</h2>
        {tooltip && (<span className="kpi-card__info-wrap" data-tooltip={tooltip} tabIndex={0}><InfoIcon /></span>)}
      </header>
      <div className="kpi-card__value">
        <span>{value}</span>
        {valueSecondary && <span className="kpi-card__value-secondary">{valueSecondary}</span>}
        {delta && (<span className={`kpi-card__delta kpi-card__delta--${delta.dir}`}>{delta.dir === 'up' ? <TrendUp /> : <TrendDown />}{delta.label}</span>)}
      </div>
      <div className="kpi-card__progress">
        <div className="kpi-card__progress-track">
          <div className="kpi-card__progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="kpi-card__progress-meta">
          <span>{progressLeftMeta ?? ''}</span>
          {progressRightMeta && <span className="numeric">{progressRightMeta}</span>}
        </div>
      </div>
    </article>
  )
}

export function PhaseView({ phase }: ViewProps) {
  const status = usePhaseStatus()
  const snaps = useDailySnapshots(21)
  const { user, account } = useAuth()
  const ps = status.data
  const copy = PHASE_COPY[phase]

  const equityValues = snaps.data?.map((s) => Number(s.endingBalance)).filter((n) => Number.isFinite(n)) ?? []
  const equityLabels = snaps.data?.map((s) => new Date(s.date).toLocaleDateString('en-IE', { month: 'short', day: 'numeric' })) ?? []

  const profitToDate = ps ? Number(ps.profitToDateEur) : 0
  const profitTarget = ps ? Number(ps.profitTargetEur) : 0
  const profitPct = profitTarget > 0 ? clampPct((profitToDate / profitTarget) * 100) : 0
  const profitDir: 'up' | 'down' = profitToDate >= 0 ? 'up' : 'down'

  const ddTotal = ps ? Number(ps.startingBalance) - Number(ps.drawdownFloor) : 0
  const ddUsed = ps ? Math.max(0, Number(ps.startingBalance) - Number(ps.balance)) : 0
  const ddPct = ddTotal > 0 ? clampPct((ddUsed / ddTotal) * 100) : 0
  const ddRiskLevel: 'low' | 'medium' | 'high' = ddPct < 33 ? 'low' : ddPct < 66 ? 'medium' : 'high'

  const betsPct = ps && ps.betsRequired > 0 ? clampPct((ps.betsPlaced / ps.betsRequired) * 100) : 0

  const daysRemaining = ps?.daysRemaining ?? null
  // Total days for the active phase — sourced from phase-status so each tier
  // shows its own deadline (Starter P1=30, Pro P1=45, Elite P1=60, etc.) instead
  // of a hard-coded Pro fallback.
  const totalDays = ps?.totalDays ?? null
  const daysElapsed = daysRemaining != null && totalDays != null ? Math.max(0, totalDays - daysRemaining) : null
  const timePct = daysElapsed != null && totalDays != null ? clampPct((daysElapsed / totalDays) * 100) : 0

  const tierCode = account?.tierCode ? account.tierCode.charAt(0).toUpperCase() + account.tierCode.slice(1) : null

  return (
    <AppShell
      page={PHASE_TO_PAGE[phase]}
      breadcrumb={<span className="topbar__breadcrumb-current">Dashboard</span>}
      tierBadge={account ? (
        <span className="tier-badge tier-badge--active">
          <span className="tier-badge__dot" aria-hidden />
          {tierCode} Challenge · {phase.toUpperCase()}
        </span>
      ) : undefined}
    >
      <header className="page-header">
        <div className="page-header__main">
          {account ? (
            <p className="page-header__eyebrow">
              <span>{tierCode} Tier</span>
              <span className="page-header__eyebrow-sep" aria-hidden />
              <span>{copy.eyebrow}</span>
            </p>
          ) : (
            <p className="page-header__eyebrow"><span>No active challenge</span></p>
          )}
          <h1 className="page-header__title">Welcome{user?.displayName ? `, ${user.displayName}` : ''}</h1>
          <p className="page-header__subtitle">
            {account ? copy.subtitle(daysRemaining) : 'Pick a tier to start your challenge — your dashboard will fill up automatically.'}
          </p>
        </div>
        <div className="page-header__actions">
          {account ? (
            <>
              <Link to="/challenge" className="btn btn--ghost" style={{ textDecoration: 'none' }}>
                <svg className="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="9" /><path d="M12 8 v.01 M12 11 v6" />
                </svg>
                Challenge rules
              </Link>
              <Link to="/sportsbook" className="btn btn--primary" style={{ textDecoration: 'none' }}>
                <svg className="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12 h18 M12 3 a14 14 0 0 1 0 18 M12 3 a14 14 0 0 0 0 18" />
                </svg>
                Place a bet
              </Link>
            </>
          ) : (
            <Link to="/challenge" className="btn btn--primary" style={{ textDecoration: 'none' }}>
              <svg className="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12 h14 M13 5 l7 7 -7 7" />
              </svg>
              Pick your tier
            </Link>
          )}
        </div>
      </header>

      <div className="dashboard">
        <section className="kpi-grid" aria-label="Challenge progress">
          <KpiCard
            label="Profit vs Target" variant="profit" tooltip="Your net profit against the phase target."
            value={(profitToDate >= 0 ? '+' : '') + fmtEur(profitToDate)}
            delta={{ dir: profitDir, label: `${ps ? (profitToDate / Math.max(1, Number(ps.startingBalance)) * 100).toFixed(2) : '0.00'}%` }}
            progressPct={profitPct}
            progressLeftMeta={`${profitPct.toFixed(0)}% to target`}
            progressRightMeta={`${fmtEur(profitToDate)} / ${fmtEur(profitTarget)}`}
          />
          <KpiCard
            label="Drawdown used" variant="risk" riskLevel={ddRiskLevel}
            tooltip="Largest peak-to-trough loss as % of starting balance."
            value={`${ddPct.toFixed(1)}%`} valueSecondary="of limit"
            progressPct={ddPct}
            progressLeftMeta={`${fmtEur(ddTotal - ddUsed)} buffer left`}
            progressRightMeta={`${fmtEur(ddUsed)} / ${fmtEur(ddTotal)}`}
          />
          <KpiCard
            label="Time remaining" variant="time" tooltip="Days left to complete this phase."
            value={daysRemaining != null ? String(daysRemaining) : '—'} valueSecondary="days left"
            progressPct={timePct}
            progressLeftMeta={daysElapsed != null && totalDays != null ? `Day ${daysElapsed} of ${totalDays}` : ''}
            progressRightMeta={`${timePct.toFixed(0)}% elapsed`}
          />
          <KpiCard
            label="Bet count" variant="count" tooltip="Minimum number of settled bets to complete the phase. Pending bets count once they settle."
            value={ps?.betsPlaced ? String(ps.betsPlaced) : '0'}
            valueSecondary={`/ ${ps?.betsRequired ?? '—'} minimum`}
            progressPct={betsPct}
            progressLeftMeta={
              ps && ps.betsPending > 0
                ? `${ps.betsPending} pending · ${Math.max(0, ps.betsRequired - ps.betsPlaced)} more to go`
                : (ps && ps.betsRequired > 0 ? `${Math.max(0, ps.betsRequired - ps.betsPlaced)} more to go` : '')
            }
            progressRightMeta={`${betsPct.toFixed(0)}%`}
          />
        </section>

        <section className="dashboard__row--split">
          <div className="chart-card" aria-labelledby="equity-title">
            <header className="chart-card__header">
              <div className="chart-card__title-group">
                <h2 className="chart-card__title" id="equity-title">Equity curve</h2>
                <p className="chart-card__subtitle">Account balance over the last 21 days</p>
              </div>
              <div className="chart-legend" role="list">
                <span className="chart-legend__item" role="listitem">
                  <span className="chart-legend__swatch chart-legend__swatch--equity" aria-hidden />
                  Balance
                </span>
              </div>
            </header>
            <div className="chart-svg-wrap" style={{ minHeight: 300 }}>
              {equityValues.length > 1 ? (
                <EquityChart labels={equityLabels} values={equityValues} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                  Not enough data yet — place a few bets to start tracking your equity curve.
                </div>
              )}
            </div>
          </div>

          <aside className="rule-card" aria-label="Challenge rule check">
            <header className="rule-card__header">
              <h2 className="rule-card__title">Rule check</h2>
              <p className="rule-card__subtitle">All hard limits, at a glance</p>
            </header>
            <ul className="rule-list">
              {ps?.reasons && ps.reasons.length > 0 ? (
                ps.reasons.map((r, i) => (
                  <li key={i} className="rule-list__item rule-list__item--warn">
                    <span className="rule-list__icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3 L22 20 H2 z" /><path d="M12 10 v4 M12 18 v.01" />
                      </svg>
                    </span>
                    <div className="rule-list__body">
                      <span className="rule-list__label">{r}</span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="rule-list__item rule-list__item--pass">
                  <span className="rule-list__icon" aria-hidden>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 13 10 18 19 7" />
                    </svg>
                  </span>
                  <div className="rule-list__body">
                    <span className="rule-list__label">All rules clean</span>
                    <span className="rule-list__detail">Keep going — no violations.</span>
                  </div>
                </li>
              )}
            </ul>
            {ps?.passEligible && (
              <div className="rule-card__footer rule-card__footer--pass">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="5 13 10 18 19 7" />
                </svg>
                Eligible to advance
              </div>
            )}
          </aside>
        </section>
      </div>
    </AppShell>
  )
}
